import { ToolbarFilter } from './data-table-toolbar';
import {
  searchKeyValueFilterOptions,
  selectKeyValuePair,
  splitKeyValuePair,
} from './key-value-filter-options';
import { Badge } from '@/components/v1/ui/badge';
import { Button } from '@/components/v1/ui/button';
import { Input } from '@/components/v1/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/v1/ui/select';
import { CheckIcon } from '@heroicons/react/20/solid';
import { Cross2Icon } from '@radix-ui/react-icons';
import { Column } from '@tanstack/react-table';
import { useState } from 'react';

type KeyValueFilterControlProps<TData> = {
  column?: Column<TData, any>;
  filter: ToolbarFilter;
};

type SelectedFiltersProps = {
  pairs: string[];
  remove: (pair: string) => void;
};

function SelectedFilters(props: SelectedFiltersProps) {
  if (props.pairs.length === 0) {
    return null;
  }

  return (
    <div className="space-y-1.5">
      <div className="text-xs font-medium text-muted-foreground">Active</div>
      {props.pairs.map((pair) => {
        const { key, value } = splitKeyValuePair(pair);
        return (
          <div
            key={pair}
            className="flex min-w-0 items-center gap-2 rounded-md border bg-muted/40 px-2 py-1.5"
          >
            <span className="min-w-0 flex-1 truncate font-mono text-xs">
              <span className="text-muted-foreground">{key}</span>
              <span className="px-1 text-muted-foreground/70">=</span>
              <span>{value}</span>
            </span>
            <Button
              type="button"
              variant="icon"
              size="xs"
              aria-label={`Remove ${key}=${value} filter`}
              onClick={() => props.remove(pair)}
            >
              <Cross2Icon className="size-3.5" />
            </Button>
          </div>
        );
      })}
    </div>
  );
}

type SuggestedFiltersProps = {
  filter: ToolbarFilter;
  pairs: string[];
  select: (pair: string) => void;
};

function SuggestedFilters(props: SuggestedFiltersProps) {
  const [search, setSearch] = useState('');
  const options = searchKeyValueFilterOptions(
    props.filter.options ?? [],
    search,
  );

  return (
    <div className="space-y-2" data-testid="metadata-filter-suggestions">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-muted-foreground">
          Available on this page
        </span>
        <span className="text-[11px] text-muted-foreground">
          {props.filter.options?.length ?? 0} pairs
        </span>
      </div>
      <Input
        autoFocus
        aria-label="Search metadata keys or values"
        placeholder="Search key or value…"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        className="h-9 text-xs"
      />
      <div className="max-h-[min(26rem,var(--radix-popover-content-available-height))] space-y-1 overflow-y-auto rounded-md border bg-muted/10 p-1.5">
        {options.map((option) => {
          const { key, value } = splitKeyValuePair(option.value);
          const selected = props.pairs.includes(option.value);
          return (
            <button
              type="button"
              key={option.value}
              aria-pressed={selected}
              className="flex w-full min-w-0 items-center gap-2 rounded px-2 py-2 text-left hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              onClick={() => props.select(option.value)}
            >
              <span className="flex size-4 shrink-0 items-center justify-center rounded border">
                {selected && <CheckIcon className="size-3" />}
              </span>
              <span className="min-w-0 flex-1 truncate font-mono text-xs">
                <span className="text-muted-foreground">{key}</span>
                <span className="px-1 text-muted-foreground/70">=</span>
                <span>{value}</span>
              </span>
              <Badge
                variant="outline"
                className="shrink-0 px-1.5 py-0 text-[10px]"
              >
                {option.count} {option.count === 1 ? 'run' : 'runs'}
              </Badge>
            </button>
          );
        })}
        {options.length === 0 && (
          <div className="px-2 py-5 text-center text-xs text-muted-foreground">
            {props.filter.options?.length
              ? 'No metadata matches this search.'
              : 'No string metadata found in the loaded runs.'}
          </div>
        )}
      </div>
    </div>
  );
}

type ManualFilterProps = {
  add: (pair: string) => void;
  filterId: string;
};

function ManualFilter(props: ManualFilterProps) {
  const [key, setKey] = useState('');
  const [value, setValue] = useState('');
  const canAdd = Boolean(key.trim() && value.trim() && !key.includes(':'));

  const add = () => {
    if (!canAdd) {
      return;
    }
    props.add(`${key.trim()}:${value.trim()}`);
    setKey('');
    setValue('');
  };

  return (
    <details className="rounded-md border bg-muted/10 px-3 py-2">
      <summary className="cursor-pointer text-xs font-medium text-muted-foreground">
        Advanced: enter key and value
      </summary>
      <div className="mt-3 space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <Input
            id={`filter-${props.filterId}-key`}
            aria-label="Metadata key"
            placeholder="Key"
            value={key}
            onChange={(event) => setKey(event.target.value)}
            className="h-8 text-xs"
          />
          <Input
            id={`filter-${props.filterId}-value`}
            aria-label="Metadata value"
            placeholder="Value"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            onKeyDown={(event) => event.key === 'Enter' && add()}
            className="h-8 text-xs"
          />
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!canAdd}
          onClick={add}
          className="w-full"
        >
          Add metadata filter
        </Button>
      </div>
    </details>
  );
}

export function KeyValueFilterControl<TData>(
  props: KeyValueFilterControlProps<TData>,
) {
  const value = props.column?.getFilterValue();
  const pairs: string[] = Array.isArray(value)
    ? value
    : value
      ? [String(value)]
      : [];

  const setPairs = (next: string[]) =>
    props.column?.setFilterValue(next.length > 0 ? next : undefined);
  const remove = (pair: string) =>
    setPairs(pairs.filter((item) => item !== pair));
  const select = (pair: string) => setPairs(selectKeyValuePair(pairs, pair));

  return (
    <div className="space-y-3">
      {props.filter.operatorConfig && pairs.length > 1 && (
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-muted-foreground">Match filters</span>
          <Select
            value={props.filter.operatorConfig.value}
            onValueChange={(operator) =>
              props.filter.operatorConfig?.onChange(operator as 'AND' | 'OR')
            }
          >
            <SelectTrigger className="h-8 w-40 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="AND">All (AND)</SelectItem>
              <SelectItem value="OR">Any (OR)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
      <SelectedFilters pairs={pairs} remove={remove} />
      <SuggestedFilters filter={props.filter} pairs={pairs} select={select} />
      <ManualFilter filterId={props.filter.columnId} add={select} />
    </div>
  );
}
